<template>
    <div v-if="isVisible">
        <div
            ref="modal"
            class="modal fade show"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="!hideHeader && title ? titleId : null"
            :aria-label="hideHeader || !title ? (title || 'Dialog') : null"
            style="display: block"
            @keydown="onKeydown"
        >
            <div :class="['modal-dialog', centered && 'modal-dialog-centered', dialogClass]" role="document">
                <div class="modal-content">
                    <div v-if="!hideHeader" class="modal-header">
                        <slot name="header"><h5 :id="titleId" class="modal-title">{{ title }}</h5></slot>
                        <button type="button" class="close" aria-label="Close" @click="hide"><span aria-hidden="true">&times;</span></button>
                    </div>
                    <div :class="['modal-body', bodyClass]"><slot /></div>
                    <div v-if="!hideFooter" class="modal-footer"><slot name="footer"><button type="button" class="btn btn-secondary" @click="hide">Close</button></slot></div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop fade show" @click="backdropClick"></div>
    </div>
</template>
<script>
import { modalController } from "./controllers";
import { emitModel } from "./model";
let nextId = 0;
const openModals = new Set();
export default {
    name: "SbModal",
    emits: ["update:modelValue", "show", "shown", "hide", "hidden"],
    provide() { return { sbModal: this }; },
    props: {
        id: { type: String, default: null },
        modelValue: { default: undefined },
        visible: { type: Boolean, default: false },
        title: { type: String, default: "" },
        centered: { type: Boolean, default: false },
        hideHeader: { type: Boolean, default: false },
        hideFooter: { type: Boolean, default: false },
        noCloseOnEsc: { type: Boolean, default: false },
        noCloseOnBackdrop: { type: Boolean, default: false },
        dialogClass: { type: [String, Array, Object], default: null },
        bodyClass: { type: [String, Array, Object], default: null },
    },
    data() {
        const modeled = this.modelValue;
        return { localVisible: modeled !== undefined ? Boolean(modeled) : this.visible, previousFocus: null, generatedId: `sb-modal-${++nextId}` };
    },
    computed: {
        isVisible() { return this.localVisible; },
        titleId() { return `${this.id || this.generatedId}-title`; },
    },
    watch: {
        modelValue(value) { if (value !== undefined) { this.setVisible(Boolean(value), false); } },
        visible(value) { if (this.modelValue === undefined) { this.setVisible(value, false); } },
    },
    mounted() { modalController.register(this.id, this); if (this.localVisible) { this.opened(); } },
    beforeUnmount() { modalController.unregister(this.id, this); this.unlockBody(); },
    methods: {
        show() { this.setVisible(true); },
        hide() { this.setVisible(false); },
        toggle() { this.setVisible(!this.localVisible); },
        setVisible(visible, emit = true) {
            if (visible === this.localVisible) { return; }
            this.$emit(visible ? "show" : "hide");
            this.localVisible = visible;
            if (emit) { emitModel(this, visible); }
            modalController.notify(this.id, visible);
            this.$nextTick(() => visible ? this.opened() : this.closed());
        },
        opened() {
            this.previousFocus = document.activeElement;
            openModals.add(this);
            document.body.classList.add("modal-open");
            this.$refs.modal?.focus();
            this.$emit("shown");
        },
        closed() {
            this.unlockBody();
            this.previousFocus?.focus?.();
            this.$emit("hidden");
        },
        unlockBody() {
            openModals.delete(this);
            if (!openModals.size) { document.body.classList.remove("modal-open"); }
        },
        restoreAfterChildModal(trigger) {
            if (!openModals.has(this)) { return; }
            document.body.classList.add("modal-open");
            this.$nextTick(() => {
                if (!openModals.has(this)) { return; }
                const modal = this.$refs.modal;
                if (trigger?.isConnected && modal?.contains(trigger)) { trigger.focus(); }
                else { modal?.focus(); }
            });
        },
        backdropClick() { if (!this.noCloseOnBackdrop) { this.hide(); } },
        onKeydown(event) {
            if (event.key === "Escape" && !this.noCloseOnEsc) { this.hide(); }
            if (event.key === "Tab") {
                const focusable = [...this.$refs.modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((element) => !element.disabled);
                if (!focusable.length) { event.preventDefault(); return; }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
            }
        },
    },
};
</script>
